// Browser-notification readiness model (GW-WEB-NOTIFICATION-PREFERENCES).
//
// Pure derivation: the Notification/Push APIs are NEVER touched here - support and permission
// arrive as injected facts, and the one permission prompt in this feature fires only from the
// component's explicit user action. Flag false means the surface is absent entirely (dark
// launch), not rendered-disabled. Honesty rules: unsupported browsers (every iOS browser tab
// today) get a plain statement and no enable theater; granted permission does not pretend
// registration exists - Web Push subscription wiring is a later, separately-assigned slice.

export type NotificationPermissionState = "default" | "granted" | "denied" | "unsupported";

export interface NotificationFacts {
  /** Server-resolved web_notifications flag (dark by default). */
  flagEnabled: boolean;
  /** Notification + serviceWorker + PushManager all present in this browser context. */
  apiSupported: boolean;
  permission: NotificationPermissionState;
}

export interface NotificationReadiness {
  kind: "hidden" | "unsupported" | "ask" | "granted-pending" | "denied";
  copy: string;
  tone: "ok" | "warn" | "muted";
  showEnableAction: boolean;
}

export function detectNotificationSupport(facts: {
  hasNotification: boolean;
  hasServiceWorker: boolean;
  hasPushManager: boolean;
}): boolean {
  return facts.hasNotification && facts.hasServiceWorker && facts.hasPushManager;
}

/** Read browser support from a window-like object. Injectable; reads only, never prompts. */
export function readBrowserNotificationFacts(win: Window | undefined = globalThis.window): {
  apiSupported: boolean;
  permission: NotificationPermissionState;
} {
  try {
    const hasNotification = Boolean(win && "Notification" in win);
    const supported = detectNotificationSupport({
      hasNotification,
      hasServiceWorker: Boolean(win && win.navigator && "serviceWorker" in win.navigator),
      hasPushManager: Boolean(win && "PushManager" in win),
    });
    if (!supported) {
      return { apiSupported: false, permission: "unsupported" };
    }
    const permission = (win as Window & { Notification: { permission: string } }).Notification.permission;
    if (permission === "granted" || permission === "denied") {
      return { apiSupported: true, permission };
    }
    return { apiSupported: true, permission: "default" };
  } catch {
    return { apiSupported: false, permission: "unsupported" };
  }
}

export function deriveNotificationReadiness(facts: NotificationFacts): NotificationReadiness {
  if (!facts.flagEnabled) {
    return { kind: "hidden", copy: "", tone: "muted", showEnableAction: false };
  }
  if (!facts.apiSupported || facts.permission === "unsupported") {
    return {
      kind: "unsupported",
      copy: "This browser does not support web notifications. The mobile app carries alerts on this device.",
      tone: "muted",
      showEnableAction: false,
    };
  }
  if (facts.permission === "granted") {
    return {
      kind: "granted-pending",
      copy: "Permission granted. Alert registration on web is coming soon - the mobile app carries alerts until then.",
      tone: "ok",
      showEnableAction: false,
    };
  }
  if (facts.permission === "denied") {
    return {
      kind: "denied",
      copy: "Notifications are blocked for this site. To change that, allow notifications in your browser settings and reload.",
      tone: "warn",
      showEnableAction: false,
    };
  }
  return {
    kind: "ask",
    copy: "Get duty alerts in this browser. Nothing is sent until your team enables web alerts end to end.",
    tone: "muted",
    showEnableAction: true,
  };
}
